import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useTenantStore } from "../stores/tenant";

interface Stats {
  callsToday: number;
  bookingsToday: number;
  pendingCallbacks: number;
  avgDurationSec: number;
  totalCostUsdToday: number;
  callsSeries: number[]; // 12 mois réels, index 11 = mois courant
}

const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export function Dashboard() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const [stats, setStats] = useState<Stats | null>(null);
  const [period, setPeriod] = useState<"12m" | "6m">("12m");

  useEffect(() => {
    if (!tenantId) return;
    api<Stats>(`/api/tenants/${tenantId}/stats`)
      .then(setStats)
      .catch(() => setStats(null));
  }, [tenantId]);

  const calls = stats?.callsToday ?? 0;
  const bookings = stats?.bookingsToday ?? 0;
  const conversion = calls > 0 ? (bookings / calls) * 100 : 0;

  // Série RÉELLE (backend). Étiquettes des mois se terminant au mois courant.
  const { series, labels } = useMemo(() => {
    const full = stats?.callsSeries ?? new Array(12).fill(0);
    const now = new Date();
    const fullLabels = Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      return MONTHS[d.getMonth()] ?? "";
    });
    const keep = period === "6m" ? 6 : 12;
    return { series: full.slice(-keep), labels: fullLabels.slice(-keep) };
  }, [stats?.callsSeries, period]);

  return (
    <div className="dashboard">
      <header className="dashboard__head">
        <div>
          <h1 className="dashboard__title">Tableau de bord</h1>
          <p className="dashboard__subtitle">Vue d'ensemble de l'activité de votre standard vocal.</p>
        </div>
      </header>

      {/* Cartes vedettes */}
      <div className="dashboard__hero">
        <StatCard
          featured
          icon="phone"
          label="Appels aujourd'hui"
          value={fmt(calls)}
          hint="Appels reçus depuis minuit"
        />
        <StatCard
          icon="calendar"
          label="Réservations"
          value={fmt(bookings)}
          hint="Confirmées automatiquement par l'assistant"
        />
        <StatCard
          icon="target"
          label="Taux de conversion"
          value={`${conversion.toFixed(1)} %`}
          hint="Appels aboutissant à une réservation"
        />
      </div>

      {/* Graphique + colonne latérale */}
      <div className="dashboard__grid">
        <section className="panel dashboard__chart">
          <div className="panel__head">
            <div>
              <h2 className="panel__title">Volume d'appels</h2>
              <p className="panel__sub">Appels traités par l'assistant</p>
            </div>
            <select
              className="chip-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value as typeof period)}
              aria-label="Période"
            >
              <option value="12m">12 mois</option>
              <option value="6m">6 mois</option>
            </select>
          </div>
          <AreaChart series={series} labels={labels} />
        </section>

        <div className="dashboard__side">
          <div className="dashboard__mini-row">
            <MiniStat
              label="Coût aujourd'hui"
              value={stats ? `$${stats.totalCostUsdToday.toFixed(2)}` : "—"}
            />
            <MiniStat
              label="Durée moyenne"
              value={stats ? `${Math.round(stats.avgDurationSec / 60)} min` : "—"}
            />
          </div>

          <section className="panel dashboard__breakdown">
            <div className="panel__head">
              <h2 className="panel__title">Répartition des appels</h2>
            </div>
            <Breakdown
              bookings={bookings}
              callbacks={stats?.pendingCallbacks ?? 0}
              total={calls}
            />
          </section>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Cartes ---------------- */

function StatCard({
  featured,
  icon,
  label,
  value,
  hint,
}: {
  featured?: boolean;
  icon: string;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className={`stat-card${featured ? " stat-card--featured" : ""}`}>
      <div className="stat-card__top">
        <span className="stat-card__icon">
          <StatIcon name={icon} />
        </span>
      </div>
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__hint">{hint}</div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="mini-stat">
      <div className="mini-stat__label">{label}</div>
      <div className="mini-stat__value">{value}</div>
    </div>
  );
}

/* ---------------- Graphique en aire ---------------- */

function AreaChart({ series, labels }: { series: number[]; labels: string[] }) {
  const total = series.reduce((s, v) => s + v, 0);
  if (total === 0) {
    return (
      <div className="area-chart area-chart--empty">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 3v18h18" />
          <path d="M7 15l4-4 3 3 5-6" />
        </svg>
        <p className="area-chart__empty-title">Pas encore d'appels</p>
        <p className="area-chart__empty-sub">La courbe se remplira dès les premiers appels reçus.</p>
      </div>
    );
  }

  const W = 640;
  const H = 240;
  const padX = 8;
  const padTop = 16;
  const padBottom = 28;
  const max = Math.max(...series) * 1.15 || 1;
  const min = 0;
  const n = series.length;

  const x = (i: number) => padX + (i / Math.max(1, n - 1)) * (W - padX * 2);
  const y = (v: number) => padTop + (1 - (v - min) / (max - min)) * (H - padTop - padBottom);

  const points: Array<[number, number]> = series.map((v, i) => [x(i), y(v)]);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${x(n - 1).toFixed(1)} ${H - padBottom} L${x(0).toFixed(1)} ${H - padBottom} Z`;

  const hiIdx = Math.max(0, series.indexOf(Math.max(...series)));
  const hiVal = series[hiIdx] ?? 0;
  const hiX = x(hiIdx);
  const hiY = y(hiVal);

  const labelStep = Math.max(1, Math.round(n / 6));

  return (
    <div className="area-chart">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="area-chart__svg" role="img" aria-label="Volume d'appels">
        <defs>
          <linearGradient id="area-fill" x1="0" y1="0" x2="0" y2="1">
            {/* Couleur du thème (Maple lime / Umbeli bleu) via --chart-accent. */}
            <stop offset="0%" style={{ stopColor: "var(--chart-accent)" }} stopOpacity="0.28" />
            <stop offset="100%" style={{ stopColor: "var(--chart-accent)" }} stopOpacity="0" />
          </linearGradient>
        </defs>

        {[0.25, 0.5, 0.75].map((t) => (
          <line
            key={t}
            x1={padX}
            x2={W - padX}
            y1={padTop + t * (H - padTop - padBottom)}
            y2={padTop + t * (H - padTop - padBottom)}
            className="area-chart__grid"
          />
        ))}

        <path d={area} fill="url(#area-fill)" />
        <path d={line} className="area-chart__line" fill="none" />

        <line x1={hiX} x2={hiX} y1={padTop} y2={H - padBottom} className="area-chart__cursor" />
        <circle cx={hiX} cy={hiY} r="5" className="area-chart__dot" />
      </svg>

      <div
        className="area-chart__tooltip"
        style={{ left: `${(hiX / W) * 100}%`, top: `${(hiY / H) * 100}%` }}
      >
        <span className="area-chart__tooltip-val">{Math.round(hiVal)}</span>
        <span className="area-chart__tooltip-lbl">appels · pic</span>
      </div>

      <div className="area-chart__axis">
        {series.map((_, i) =>
          i % labelStep === 0 ? (
            <span key={i} style={{ left: `${(x(i) / W) * 100}%` }}>
              {labels[i] ?? ""}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}

/* ---------------- Répartition (bulles) ---------------- */

function Breakdown({ bookings, callbacks, total }: { bookings: number; callbacks: number; total: number }) {
  if (total === 0) {
    return (
      <div className="breakdown breakdown--empty">
        <p className="breakdown__empty-title">Aucun appel aujourd'hui</p>
        <p className="breakdown__empty-sub">La répartition apparaîtra avec les premiers appels.</p>
      </div>
    );
  }

  const info = Math.max(total - bookings - callbacks, 0);
  const segments = [
    { key: "Réservations", value: bookings, color: "var(--seg-1)" },
    { key: "Infos / FAQ", value: info, color: "var(--seg-2)" },
    { key: "Rappels", value: callbacks, color: "var(--seg-3)" },
  ].sort((a, b) => b.value - a.value);

  const sum = segments.reduce((s, x) => s + x.value, 0) || 1;

  const layout = [
    { cx: 38, cy: 52, base: 34 },
    { cx: 76, cy: 40, base: 24 },
    { cx: 66, cy: 74, base: 17 },
  ];

  return (
    <div className="breakdown">
      <svg viewBox="0 0 110 110" className="breakdown__viz" role="img" aria-label="Répartition des appels">
        {segments.map((seg, i) => {
          const l = layout[i] ?? layout[0]!;
          const share = seg.value / sum;
          const r = Math.max(10, l.base * (0.55 + share));
          return (
            <g key={seg.key}>
              <circle cx={l.cx} cy={l.cy} r={r} fill={seg.color} />
              <text x={l.cx} y={l.cy + 4} className="breakdown__pct" textAnchor="middle">
                {Math.round(share * 100)}%
              </text>
            </g>
          );
        })}
      </svg>

      <ul className="breakdown__legend">
        {segments.map((seg, i) => (
          <li key={seg.key}>
            <span className="breakdown__swatch" data-i={i} />
            <span className="breakdown__legend-label">{seg.key}</span>
            <span className="breakdown__legend-value">{fmt(seg.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------------- Icônes ---------------- */

function StatIcon({ name }: { name: string }) {
  const c = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (name) {
    case "phone":
      return (
        <svg {...c}>
          <path d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 2 6a2 2 0 0 1 2-2z" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...c}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 3v4M16 3v4" />
        </svg>
      );
    case "target":
      return (
        <svg {...c}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      );
    default:
      return <svg {...c} />;
  }
}

/* ---------------- Utilitaires ---------------- */

function fmt(n: number): string {
  return n.toLocaleString("fr-CA");
}
